import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";
import { CursorReactiveModule } from "./CursorReactiveModule";
import { PhysicsBody } from "../../core/physics/PhysicsBody";
import { SpringForce } from "../../core/physics/forces/SpringForce";

interface TiltBodies {
  bodyX: PhysicsBody;
  bodyY: PhysicsBody;
  springX: SpringForce;
  springY: SpringForce;
  prevCssX: number;
  prevCssY: number;
}

const tiltBodies = new WeakMap<StringObject, TiltBodies>();

const getBodies = (object: StringObject): TiltBodies => {
  let bodies = tiltBodies.get(object);
  if (!bodies) {
    const springX = new SpringForce(0, 0);
    const springY = new SpringForce(0, 0);
    const bodyX = new PhysicsBody();
    bodyX.addForce(springX);
    const bodyY = new PhysicsBody();
    bodyY.addForce(springY);
    bodies = { bodyX, bodyY, springX, springY, prevCssX: NaN, prevCssY: NaN };
    tiltBodies.set(object, bodies);
  }
  return bodies;
};

export class StringTilt extends CursorReactiveModule {
  constructor(context: any) {
    super(context);
    this.htmlKey = "tilt";
    this._type = 2;

    this.cssProperties = [
      { name: "--tilt-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--tilt-y", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.nearOnly = true;
    this.useAllObjects = false;

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap.push(
      { key: "tilt-max", type: "number", fallback: this.settings["tilt-max"] ?? 15 },
      { key: "tilt-tension", type: "number", fallback: this.settings["tilt-tension"] ?? 0.18 },
      { key: "tilt-friction", type: "number", fallback: this.settings["tilt-friction"] ?? 0.28 },
      {
        key: "tilt-return-tension",
        type: "number",
        fallback: this.settings["tilt-return-tension"] ?? 0.1,
      },
      {
        key: "tilt-return-friction",
        type: "number",
        fallback: this.settings["tilt-return-friction"] ?? 0.2,
      },
      {
        key: "tilt-sleep-epsilon",
        type: "number",
        fallback: this.settings["tilt-sleep-epsilon"] ?? 0.005,
      },
      {
        key: "tilt-threshold",
        type: "number",
        fallback: this.settings["tilt-threshold"] ?? 0.01,
      },
      {
        key: "tilt-invert",
        type: "boolean",
        fallback: this.settings["tilt-invert"] ?? false,
      },
    );
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("__needs-tilt-update", false);
    object.setProperty("__next-tilt-x", 0);
    object.setProperty("__next-tilt-y", 0);
    object.setProperty("tilt-attached", false);
    object.setProperty("event-tilt-name", this.getObjectEventName(object, "tilt:update"));
  }

  override onMutate(data: StringData): void {
    if (!this.enabled) return;
    super.onMutate(data);

    const targets = this.getCursorTargets(false);
    const list = targets.length > 0 ? targets : this.getCursorTargets(true);

    for (let i = 0; i < list.length; i++) {
      this.stepObject(list[i]);
    }

    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      if (!list.includes(object)) {
        this.stepObject(object);
      }
    }

    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const needsUpdate = object.getProperty<boolean>("__needs-tilt-update") === true;
      if (!needsUpdate) continue;

      const x = object.getProperty<number>("__next-tilt-x") ?? 0;
      const y = object.getProperty<number>("__next-tilt-y") ?? 0;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVar(el, "--tilt-x", x);
        styleTxn.setVar(el, "--tilt-y", y);
      });

      const bodies = getBodies(object);
      bodies.prevCssX = x;
      bodies.prevCssY = y;

      const eventName = object.getProperty<string>("event-tilt-name");
      if (eventName) {
        this.events.emit(eventName, { x, y });
      }

      object.setProperty("__needs-tilt-update", false);
    }
  }

  private stepObject(object: StringObject): void {
    const dx = object.getProperty<number>("dx");
    const dy = object.getProperty<number>("dy");
    const attached = Number.isFinite(dx) && Number.isFinite(dy);

    object.setProperty("tilt-attached", attached);

    const bodies = getBodies(object);

    const tiltMax = object.getProperty<number>("tilt-max") ?? 15;
    const sleepEpsilon = object.getProperty<number>("tilt-sleep-epsilon") ?? 0.005;
    const threshold = object.getProperty<number>("tilt-threshold") ?? 0.01;
    const invert = object.getProperty<boolean>("tilt-invert") ?? false;

    let targetX = 0;
    let targetY = 0;
    let tension: number;
    let friction: number;

    if (attached) {
      const el = object.htmlElement as HTMLElement;
      const w = el.offsetWidth || 1;
      const h = el.offsetHeight || 1;

      const nx = Math.max(-1, Math.min(1, (dx ?? 0) / (w / 2)));
      const ny = Math.max(-1, Math.min(1, (dy ?? 0) / (h / 2)));

      const sign = invert ? -1 : 1;
      targetX = ny * tiltMax * sign;
      targetY = -nx * tiltMax * sign;

      tension = object.getProperty<number>("tilt-tension") ?? 0.18;
      friction = object.getProperty<number>("tilt-friction") ?? 0.28;
    } else {
      tension = object.getProperty<number>("tilt-return-tension") ?? 0.1;
      friction = object.getProperty<number>("tilt-return-friction") ?? 0.2;
    }

    bodies.springX.tension = tension;
    bodies.springX.target = targetX;
    bodies.springY.tension = tension;
    bodies.springY.target = targetY;
    bodies.bodyX.friction = friction;
    bodies.bodyY.friction = friction;

    bodies.bodyX.step();
    bodies.bodyY.step();

    const isSleeping =
      !attached &&
      Math.abs(bodies.bodyX.value) < sleepEpsilon &&
      Math.abs(bodies.bodyY.value) < sleepEpsilon &&
      Math.abs(bodies.bodyX.velocity) < sleepEpsilon &&
      Math.abs(bodies.bodyY.velocity) < sleepEpsilon;

    if (isSleeping) {
      bodies.bodyX.reset();
      bodies.bodyY.reset();
      object.setProperty("__needs-tilt-update", false);
      return;
    }

    const cssX = Math.round(bodies.bodyX.value * 100) / 100;
    const cssY = Math.round(bodies.bodyY.value * 100) / 100;
    const prevX = bodies.prevCssX;
    const prevY = bodies.prevCssY;
    const changed =
      Number.isNaN(prevX) ||
      Number.isNaN(prevY) ||
      Math.abs(cssX - prevX) > threshold ||
      Math.abs(cssY - prevY) > threshold;

    object.setProperty("__needs-tilt-update", changed);
    if (changed) {
      object.setProperty("__next-tilt-x", cssX);
      object.setProperty("__next-tilt-y", cssY);
    }
  }
}
